import { type FC } from "react";

import { faSpinner } from "@fortawesome/pro-duotone-svg-icons/faSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { BillableSection } from "./billable_section";
import { LettersSection } from "./letters_section";
import { PagesSection } from "./pages_section";
import type {
  BillableData,
  LetterCounts,
  LetterScope,
  PageCounts,
} from "./types";

export type { LetterScope };

/**
 * Props grouped by section: a section renders iff its config is present.
 * Replaces the previous flat 13-prop API; the `show: { ... }` boolean
 * map is implicit from prop presence (CLAUDE.md ≤10-props ceiling).
 */
export type LettersConfig = {
  counts: LetterCounts;
  interactive?: "filter" | "none";
  activeScope?: LetterScope | null;
  onScopeChange?: (scope: LetterScope | null) => void;
};

export type PagesConfig = {
  counts: PageCounts;
  display?: "stat-row" | "bars";
  // Override for the section heading text. Records-processed card passes
  // "Pages processed" to match the card-header voice; default "Pages".
  heading?: string;
};

export type BillableConfig = {
  data: BillableData;
  // `compact` collapses BillableSection to a single inline line — used by
  // the Records-processed card. Default `full` keeps the prominent 3-line
  // form for the Billing By Project drill-in.
  variant?: "compact" | "full";
};

// `chrome="section-bar"` adds the white card framing used between toolbar
// rows on Report Review. `bare` is the default (consumer wraps as needed).
export type ProcessingStatsCardProps = {
  letters?: LettersConfig;
  pages?: PagesConfig;
  billable?: BillableConfig;
  primarySection: "letters" | "pages" | "billable";
  merging?: boolean;
  chrome?: "bare" | "section-bar";
};

export const ProcessingStatsCard: FC<ProcessingStatsCardProps> = ({
  letters,
  pages,
  billable,
  primarySection,
  merging,
  chrome = "bare",
}) => {
  const body = merging ? (
    <MergingState />
  ) : (
    <div className="space-y-3" role="status" aria-live="polite">
      {letters && (
        <LettersSection
          counts={letters.counts}
          interactive={letters.interactive ?? "none"}
          activeScope={letters.activeScope ?? null}
          onScopeChange={letters.onScopeChange}
        />
      )}
      {pages && (
        <>
          {letters && <Divider />}
          <PagesSection
            counts={pages.counts}
            display={pages.display ?? "stat-row"}
            primary={primarySection === "pages"}
            heading={pages.heading}
            fullPageCount={billable?.data.fullPageCount}
            condensedPageCount={billable?.data.condensedPageCount}
            condensedMultiplier={billable?.data.condensedMultiplier}
          />
        </>
      )}
      {billable && (
        <>
          {(letters || pages) && <Divider />}
          <BillableSection
            data={billable.data}
            primary={primarySection === "billable"}
            variant={billable.variant ?? "full"}
          />
        </>
      )}
    </div>
  );

  if (chrome === "section-bar") {
    return (
      <div className="border-b border-slate-200 bg-white px-4 py-3">{body}</div>
    );
  }
  return body;
};

const Divider: FC = () => <hr className="border-slate-100" />;

const MergingState: FC = () => (
  <div className="flex items-center gap-2 px-1 py-3 text-sm text-indigo-600">
    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
    <span className="font-medium">Matching letters against prior review…</span>
  </div>
);
